import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Heart } from "lucide-react";
import Navbar from "components/layout/Navbar";
import Footer from "components/layout/Footer";
import { useCart, useAuth } from "App";

const CartPage = () => {
  const { cart, updateCartItem, removeFromCart, cartTotal, cartItemCount } =
    useCart();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleQuantityChange = (item, change) => {
    const newQuantity = item.quantity + change;
    if (newQuantity < 1) return;
    updateCartItem(item.product_id, newQuantity, item.size, item.color, item.variant_id);
  };

  const handleCheckout = () => {
    navigate("/checkout");
    // if (!user) {
    //   login();
    // } else {
    //   navigate("/checkout");
    // }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 }
  };

  if (cartItemCount === 0) {
    return (
      <div className="bg-[#F4EBE1] text-[#4F3535] min-h-screen">
        <Navbar />
        <div className="pt-32 pb-20 min-h-[70vh] flex items-center justify-center">
          <motion.div className="text-center" {...fadeInUp}>
            <div className="w-20 h-20 mx-auto mb-8 rounded-full border border-[#4F3535]/15 flex items-center justify-center">
              <ShoppingBag className="w-8 h-8 text-[#4F3535]/30" />
            </div>
            <h1 className="font-serif text-3xl text-[#4F3535] mb-3">
              Your Cart is Empty
            </h1>
            <p className="text-[#8A7B7B] mb-8 font-serif italic">
              Looks like you haven't added anything yet.
            </p>
            <Link
              to="/shop"
              className="bg-[#6A4B4B] text-white hover:bg-[#4F3535] transition-colors duration-300 px-12 py-4 tracking-[0.2em] uppercase text-sm font-medium inline-flex items-center gap-2"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#F4EBE1] text-[#4F3535] min-h-screen">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h1 className="font-serif text-4xl md:text-5xl font-medium text-[#4F3535] leading-[1.1] mb-6">
              Shopping Cart
            </h1>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
              <Heart className="w-4 h-4 text-[#6A4B4B] fill-[#6A4B4B]" />
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {cart.items?.map((item, index) => (
                  <motion.div
                    key={`${item.product_id}-${item.size}-${item.color}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-6 bg-white/60 backdrop-blur-sm p-4 border border-[#EAE2DA]"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Image */}
                    <Link
                      to={`/product/${item.product_id}`}
                      className="flex-shrink-0"
                    >
                      <img
                        src={
                          item.image ||
                          "https://images.unsplash.com/photo-1596484552993-aec4311d3381?w=200"
                        }
                        alt={item.name}
                        className="w-24 h-32 object-cover"
                      />
                    </Link>

                    {/* Details */}
                    <div className="flex-1">
                      <Link to={`/product/${item.product_id}`}>
                        <h3 className="font-medium text-[#4F3535] hover:text-[#6A4B4B] transition-colors">
                          {item.name}
                        </h3>
                      </Link>
                      <div className="flex flex-wrap gap-2 mt-1 text-sm text-[#8A7B7B]">
                        {item.size && <span>Size: {item.size}</span>}
                        {item.color && <span>• Color: {item.color}</span>}
                      </div>
                      <p className="font-semibold text-[#4F3535] mt-2 price-tag">
                        {formatPrice(item.price)}
                      </p>

                      {/* Quantity & Remove */}
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleQuantityChange(item, -1)}
                            className="w-8 h-8 flex items-center justify-center border border-[#EAE2DA] hover:border-[#6A4B4B] hover:bg-[#6A4B4B]/5 transition-all text-[#4F3535]"
                            data-testid={`decrease-${item.product_id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center text-[#4F3535]">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item, 1)}
                            className="w-8 h-8 flex items-center justify-center border border-[#EAE2DA] hover:border-[#6A4B4B] hover:bg-[#6A4B4B]/5 transition-all text-[#4F3535]"
                            data-testid={`increase-${item.product_id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() =>
                            removeFromCart(
                              item.product_id,
                              item.size,
                              item.color,
                              item.variant_id
                            )
                          }
                          className="text-[#8A7B7B] hover:text-[#E57373] transition-colors"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/shop"
                className="mt-8 inline-flex items-center gap-2 text-[#6A4B4B] hover:text-[#4F3535] transition-colors text-sm tracking-widest uppercase"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Continue Shopping
              </Link>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white/60 backdrop-blur-sm border border-[#EAE2DA] p-6 sticky top-28">
                <h2 className="font-serif text-xl text-[#4F3535] mb-6">
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-[#8A7B7B]">
                    <span>Subtotal ({cartItemCount} items)</span>
                    <span>{formatPrice(cartTotal)}</span>
                  </div>
                  <div className="flex justify-between text-[#8A7B7B]">
                    <span>Delivery</span>
                    <span className="text-[#8DA399] italic text-sm">
                      Calculated at checkout
                    </span>
                  </div>
                </div>

                <div className="border-t border-[#EAE2DA] pt-4 mb-6">
                  <div className="flex justify-between text-lg font-semibold text-[#4F3535]">
                    <span>Total</span>
                    <span className="price-tag">{formatPrice(cartTotal)}</span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full bg-[#6A4B4B] text-white hover:bg-[#4F3535] transition-all duration-300 py-4 tracking-[0.2em] uppercase text-sm font-medium"
                  data-testid="checkout-btn"
                >
                  {"Proceed to Checkout"}
                </button>

                {/* Payment Methods */}
                <div className="mt-6 pt-6 border-t border-[#EAE2DA]">
                  <p className="text-xs text-[#8A7B7B] text-center mb-3 tracking-widest uppercase">
                    We Accept
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-xs bg-white/80 px-3 py-1 border border-[#EAE2DA] text-[#4F3535]">
                      M-Pesa
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
